import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, MapPin, Users, Bed, Bath, Heart, Share2, Loader2, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { toast } from 'sonner';
import { usePropertyTranslation } from '@/hooks/usePropertyTranslation';
import { useConfetti } from '@/hooks/useConfetti';
import { api } from '@/lib/api';
import { getPlanName } from '@/lib/planConfig';

export const PropertyCard = ({ property, showCompare = false, onCompareToggle, index = 0 }) => {
  const { t } = useTranslation();
  const [isFavorite, setIsFavorite] = useState(false);
  const [isFavoriteLoading, setIsFavoriteLoading] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [justFavorited, setJustFavorited] = useState(false);
  const favoriteButtonRef = useRef(null);
  const { fireFromElement, fireHearts } = useConfetti();
  
  const { translatedTitle, isLoading: isTranslating } = usePropertyTranslation(property);
  
  const {
    id,
    title,
    city,
    state,
    price_per_night,
    price,
    nightly_price_suggestion,
    bedrooms,
    bathrooms,
    max_guests,
    images = [],
    is_sponsored,
    is_verified,
    plan_type,
    host,
  } = property;
  
  // Use price_per_night as the single source of truth, fallback to legacy fields for backward compatibility
  const displayPrice = price_per_night || price || nightly_price_suggestion || 0;

  const displayTitle = translatedTitle || title;
  const isSuperHost = host?.super_host || false;
  const rating = host?.average_rating;  // Can be null/undefined when no reviews
  // Check both field names as backend may use either
  const reviewCount = host?.review_count || host?.total_reviews || 0;
  // Only show rating if there are actual reviews (reviewCount > 0)
  // Do NOT fallback to rating > 0 because rating can be default 5.0 without reviews
  const hasReviews = reviewCount > 0;

  const handleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const token = localStorage.getItem('magicstay_token');
    if (!token) {
      toast.error(t('common.loginToFavorite'));
      return;
    }
    
    setIsFavoriteLoading(true);
    
    try {
      if (isFavorite) {
        // Remove from favorites
        await api.delete(`/favorites/${id}`);
        setIsFavorite(false);
        toast.success(t('common.removedFromFavorites'));
        
        // Also update localStorage for offline sync
        const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
        const idx = favorites.findIndex(f => f.id === id);
        if (idx > -1) {
          favorites.splice(idx, 1);
          localStorage.setItem('favorites', JSON.stringify(favorites));
        }
      } else {
        // Add to favorites
        await api.post(`/favorites/${id}`);
        setIsFavorite(true);
        setJustFavorited(true);
        setTimeout(() => setJustFavorited(false), 600);
        toast.success(t('common.addedToFavorites'));
        
        // Also update localStorage for offline sync
        const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
        if (!favorites.some(f => f.id === id)) {
          favorites.push(property);
          localStorage.setItem('favorites', JSON.stringify(favorites));
        }
        
        // Fire confetti celebration!
        if (favoriteButtonRef.current) {
          fireFromElement(favoriteButtonRef.current, 'favorite');
        } else {
          fireHearts();
        }
      }
      
      window.dispatchEvent(new CustomEvent('favoritesChanged'));
    } catch (error) {
      console.error('Favorite error:', error);
      // Fallback to localStorage if API fails
      const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
      const idx = favorites.findIndex(f => f.id === id);
      
      if (idx > -1) {
        favorites.splice(idx, 1);
        setIsFavorite(false);
        toast.success(t('common.removedFromFavorites'));
      } else {
        favorites.push(property);
        setIsFavorite(true);
        toast.success(t('common.addedToFavorites'));
      }
      
      localStorage.setItem('favorites', JSON.stringify(favorites));
      window.dispatchEvent(new CustomEvent('favoritesChanged'));
    } finally {
      setIsFavoriteLoading(false);
    }
  };

  const handleShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const shareUrl = `${window.location.origin}/property/${id}`;
    const shareData = {
      title: title,
      text: `${t('common.checkOut')} ${city}, ${state}`,
      url: shareUrl
    };
    
    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        toast.success(t('common.sharedSuccess'));
        return;
      }
      
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        toast.success(t('common.linkCopied'));
        return;
      }
      
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success(t('common.linkCopied'));
    } catch (error) {
      if (error.name !== 'AbortError') {
        toast.info(`Link: ${shareUrl}`, { duration: 10000 });
      }
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('magicstay_token');
    setIsLoggedIn(!!token);
    
    const checkFavoriteStatus = () => {
      if (token && id) {
        // Check favorite status from API
        api.get(`/favorites/check/${id}`)
          .then(res => {
            setIsFavorite(res.data.is_favorite);
          })
          .catch(() => {
            // Fallback to localStorage
            const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
            setIsFavorite(favorites.some(f => f.id === id));
          });
      }
    };
    
    checkFavoriteStatus();
    
    // Listen for favorite changes from other components
    const handleFavoritesChanged = () => {
      checkFavoriteStatus();
    };
    
    // Re-check when page becomes visible (user navigates back)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkFavoriteStatus();
      }
    };
    
    // Re-check when window gains focus (for SPA navigation)
    const handleFocus = () => {
      checkFavoriteStatus();
    };
    
    window.addEventListener('favoritesChanged', handleFavoritesChanged);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    const compareList = JSON.parse(localStorage.getItem('compare_properties') || '[]');
    setIsSelected(compareList.some(p => p.id === id));
    
    return () => {
      window.removeEventListener('favoritesChanged', handleFavoritesChanged);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [id]);
  
  const handleCompareToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const compareList = JSON.parse(localStorage.getItem('compare_properties') || '[]');
    const idx = compareList.findIndex(p => p.id === id);
    
    if (idx > -1) {
      compareList.splice(idx, 1);
      setIsSelected(false);
      toast.success(t('common.removedFromCompare'));
    } else {
      if (compareList.length >= 4) {
        toast.error(t('common.maxCompare'));
        return;
      }
      compareList.push(property);
      setIsSelected(true);
      toast.success(t('common.addedToCompare'));
    }
    
    localStorage.setItem('compare_properties', JSON.stringify(compareList));
    if (onCompareToggle) onCompareToggle(compareList);
  };

  // Plan badge with sparkle effects
  const getPlanBadge = () => {
    const { i18n } = useTranslation();
    const lang = i18n.language?.startsWith('pt') ? 'pt' : 'en';
    
    if (plan_type === 'diamond') {
      return (
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="absolute top-3 right-3 bg-gradient-to-r from-[#FFE9A3] via-[#FFC857] to-[#FFE9A3] text-[#0B1220] px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-[0_4px_16px_rgba(255,200,87,0.5)] diamond-sparkle z-20"
        >
          <motion.div
            animate={{ rotate: [0, 15, -15, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Sparkles className="w-3.5 h-3.5" />
          </motion.div>
          {getPlanName('diamond', lang).toUpperCase()}
        </motion.div>
      );
    }
    if (plan_type === 'gold') {
      return (
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="absolute top-3 right-3 bg-gradient-to-r from-amber-500 to-yellow-400 text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-[0_4px_12px_rgba(245,158,11,0.4)] z-20"
        >
          <Star className="w-3.5 h-3.5 fill-current" />
          {getPlanName('gold', lang).toUpperCase()}
        </motion.div>
      );
    }
    if (plan_type === 'premium') {
      return (
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="absolute top-3 right-3 bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-[0_4px_12px_rgba(139,92,246,0.4)] z-20"
        >
          <Star className="w-3.5 h-3.5 fill-current" />
          {getPlanName('premium', lang).toUpperCase()}
        </motion.div>
      );
    }
    return null;
  };

  return (
    <motion.div 
      className="relative"
      data-testid="property-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Card className="group overflow-hidden bg-white rounded-2xl shadow-[0_2px_8px_rgba(8,23,47,0.06)] hover:shadow-[0_16px_40px_rgba(8,23,47,0.12)] transition-all duration-400 hover:-translate-y-2 border-0 ring-1 ring-gray-100 hover:ring-[#00C2D1]/30">
        {showCompare && (
          <div className="absolute top-3 left-3 z-30">
            <motion.div 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-2 bg-white/95 backdrop-blur-md rounded-full px-3 py-2 shadow-lg cursor-pointer hover:bg-white border border-gray-100"
              onClick={handleCompareToggle}
              data-testid="compare-checkbox-container"
            >
              <Checkbox 
                checked={isSelected}
                onCheckedChange={handleCompareToggle}
                className="pointer-events-none"
                data-testid="compare-checkbox"
              />
              <span className="text-xs font-medium text-gray-700">{t('common.compare')}</span>
            </motion.div>
          </div>
        )}

        {!showCompare && (
          <div className="absolute top-3 left-3 z-30 flex gap-2">
            {isLoggedIn && (
              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                <Button
                  ref={favoriteButtonRef}
                  type="button"
                  size="icon"
                  variant="secondary"
                  className={`h-9 w-9 rounded-full bg-white/95 backdrop-blur-md hover:bg-white shadow-lg border border-gray-100 ${
                    justFavorited ? 'heart-beat' : ''
                  }`}
                  onClick={handleFavorite}
                  data-testid="favorite-button"
                  aria-label={t('property.addToFavorites')}
                >
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={isFavorite ? 'filled' : 'empty'}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      <Heart 
                        className={`h-4 w-4 transition-colors duration-300 ${
                          isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-600'
                        }`}
                      />
                    </motion.div>
                  </AnimatePresence>
                </Button>
              </motion.div>
            )}
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
              <Button
                type="button"
                size="icon"
                variant="secondary"
                className="h-9 w-9 rounded-full bg-white/95 backdrop-blur-md hover:bg-white shadow-lg border border-gray-100"
                onClick={handleShare}
                data-testid="share-button"
                aria-label={t('common.share')}
              >
                <Share2 className="h-4 w-4 text-gray-600" />
              </Button>
            </motion.div>
          </div>
        )}

        <Link to={`/property/${id}`} className="cursor-pointer">
          <div className="aspect-[4/3] bg-gray-100 overflow-hidden relative">
            {images.length > 0 ? (
              <Carousel className="w-full h-full">
                <CarouselContent>
                  {images.slice(0, 5).map((img, idx) => (
                    <CarouselItem key={idx}>
                      <motion.img
                        src={img.image_url}
                        alt={`${title} - ${idx + 1}`}
                        className="h-full w-full object-cover"
                        loading="lazy"
                        decoding="async"
                        animate={{ scale: isHovered ? 1.08 : 1 }}
                        transition={{ duration: 0.5 }}
                      />
                    </CarouselItem>
                  ))}
                </CarouselContent>
                {images.length > 1 && (
                  <>
                    <CarouselPrevious className="left-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <CarouselNext className="right-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </>
                )}
              </Carousel>
            ) : (
              <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-50">
                <MapPin className="h-12 w-12 text-gray-300" />
              </div>
            )}
            
            {getPlanBadge()}
            
            {/* Image count indicator */}
            {images.length > 1 && (
              <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-medium">
                1/{images.length}
              </div>
            )}
          </div>

          <CardContent className="p-5 space-y-3">
            {/* Badges row */}
            <div className="flex items-center gap-2 flex-wrap min-h-[24px]">
              {is_sponsored && (
                <Badge
                  variant="outline"
                  className="bg-amber-50 text-amber-700 border-amber-200 text-xs font-medium"
                  data-testid="badge-sponsored"
                >
                  {t('common.sponsored')}
                </Badge>
              )}
              {isSuperHost && (
                <Badge
                  variant="outline"
                  className="bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-700 border-amber-200 text-xs font-medium"
                  data-testid="badge-superhost"
                >
                  ⭐ {t('common.superHost')}
                </Badge>
              )}
              {is_verified && (
                <Badge
                  variant="outline"
                  className="bg-cyan-50 text-cyan-700 border-cyan-200 text-xs font-medium"
                  data-testid="badge-verified"
                >
                  ✓ {t('common.verified')}
                </Badge>
              )}
            </div>

            {/* Title & Rating */}
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-semibold text-[15px] text-gray-900 line-clamp-2 flex-1 leading-snug">
                {isTranslating ? (
                  <span className="flex items-center gap-1.5">
                    <Loader2 className="w-3 h-3 animate-spin text-[#00C2D1]" />
                    <span className="text-gray-500">{title}</span>
                  </span>
                ) : displayTitle}
              </h3>
              {hasReviews && rating !== null && rating !== undefined ? (
                <div className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-lg flex-shrink-0">
                  <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                  <span className="text-sm font-semibold text-gray-800">{Number(rating).toFixed(1)}</span>
                </div>
              ) : (
                <div className="flex items-center gap-1 bg-emerald-50 px-2 py-1 rounded-lg flex-shrink-0">
                  <Sparkles className="w-3.5 h-3.5 text-emerald-500" />
                  <span className="text-sm font-medium text-emerald-700">{t('common.new') || 'Novo'}</span>
                </div>
              )}
            </div>

            {/* Location */}
            <div className="flex items-center gap-1.5 text-sm text-gray-500">
              <MapPin className="w-4 h-4 text-[#00C2D1]" />
              <span className="line-clamp-1">
                {city}, {state}
              </span>
            </div>

            {/* Features */}
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1.5 bg-gray-50 px-2.5 py-1 rounded-lg">
                <Users className="w-4 h-4 text-gray-400" />
                <span className="font-medium">{max_guests}</span>
              </div>
              <div className="flex items-center gap-1.5 bg-gray-50 px-2.5 py-1 rounded-lg">
                <Bed className="w-4 h-4 text-gray-400" />
                <span className="font-medium">{bedrooms}</span>
              </div>
              <div className="flex items-center gap-1.5 bg-gray-50 px-2.5 py-1 rounded-lg">
                <Bath className="w-4 h-4 text-gray-400" />
                <span className="font-medium">{bathrooms}</span>
              </div>
            </div>

            {/* Price & CTA */}
            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
              <div>
                <span className="text-2xl font-bold text-gray-900">${displayPrice}</span>
                <span className="text-sm text-gray-500 ml-1">/ {t('common.night')}</span>
              </div>
              <motion.span 
                className="text-sm text-[#00C2D1] font-semibold flex items-center gap-1"
                data-testid="property-card-view-link"
              >
                {t('common.viewDetails')}
                <motion.span
                  animate={{ x: isHovered ? 4 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  →
                </motion.span>
              </motion.span>
            </div>
          </CardContent>
        </Link>
      </Card>
    </motion.div>
  );
};
